import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import chatMessageTextStyles from 'css/src/components/agent/ChatMessageText.module.scss';

import React from 'react';
import { VideoData } from '@lumen5/luminary';

import { z } from 'zod';

import MarkdownWithDiff from '../components/MarkdownWithDiff';
import { IconRegistry } from '../registries/IconRegistry';
import type { CustomRendererComponentProps, ToolExecutionContext } from '../types';

import { isNonRecommendable } from '../video-state/serialization/assetSerializer';

import {
    applyResolvedAssetTimingOperation,
    hasAssetTimingArgs,
    planAssetTiming,
    resolveAssetTimingOperation,
    withTimingNote,
} from './asset-timing-placement';
import { ASSET_TIMING_SCHEMA_SHAPE } from './asset-timing-schema';
import { nonEditableAssetError } from './non-editable-asset';
import { resolveSceneHandle } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';

import BackgroundAsset from 'js/src/components/app/BackgroundAsset';
import { checkpointVideo } from 'js/src/data/video-data';
import { saveRawLuminary } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

export const setIconSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    asset_name: z.string().describe('The asset name (e.g. icon_1)'),
    media_id: z
        .string()
        .optional()
        .describe(
            `The id of an icon from a ${TOOL_NAMES.searchIcons} result (a 6-character code like "ArBvn1"). Never invent one or pass an icon name.`,
        ),
    ...ASSET_TIMING_SCHEMA_SHAPE,
});
export type SetIconArgs = z.infer<typeof setIconSchema>;

function unknownIconError(mediaId: string): string {
    const asWords = mediaId.replace(/[_-]+/g, ' ').trim();
    return `Error: Unknown icon ${mediaId}. Icon ids come only from ${TOOL_NAMES.searchIcons} results — call ${TOOL_NAMES.searchIcons} with "${asWords}" and use a returned id.`;
}

export class SetIconTool extends VideoStateTool<typeof setIconSchema> {
    protected override readonly schema = setIconSchema;
    protected override readonly toolName = TOOL_NAMES.setIcon;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription = 'Set icon for a specific scene and asset';

    public override customRenderer = SetIconResultRenderer;

    public override getToolDisplayName(): string {
        return 'Setting icon';
    }

    public get statusIcon(): string {
        return 'emoji_symbols';
    }

    public async execute(args: SetIconArgs, context: ToolExecutionContext) {
        const { asset_name, media_id } = args;
        const { parentActionGroup } = context;
        const timingRequested = hasAssetTimingArgs(args);

        const changes: string[] = [];
        try {
            if (media_id === undefined && !timingRequested) {
                return 'Error: At least one of media_id, start_words or start_time_ms must be provided';
            }

            const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            const scene = this.luminary.scenes[scene_index];
            const asset = scene.getAssetByName(asset_name);
            if (!asset) {
                return `Error: No asset found with name ${asset_name} in scene ${sceneShortId}`;
            }
            if (!asset.isEditable) {
                return nonEditableAssetError(asset_name, sceneShortId);
            }

            if (isNonRecommendable(scene, asset_name, this.getState())) {
                return nonEditableAssetError(asset_name, sceneShortId);
            }

            if (!VideoData.isIconAsset(asset)) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} is not an icon asset`;
            }

            let iconSourceData;
            if (media_id !== undefined) {
                const iconRegistry = IconRegistry.getInstance();
                iconSourceData = iconRegistry.hasItem(media_id)
                    ? iconRegistry.getIconSourceData(media_id)
                    : undefined;
                if (!iconSourceData) {
                    return unknownIconError(media_id);
                }
            }
            const iconAlreadyApplied =
                iconSourceData !== undefined && asset.sourceData?.url === iconSourceData.url;
            if (iconAlreadyApplied && !timingRequested) {
                return `No change: ${asset_name} already shows icon ${media_id} in scene ${sceneShortId}.`;
            }

            // Resolve timing before any edit lands. A timing-only call with bad timing fails
            // outright; alongside an icon swap the timing is adjusted or skipped with a note.
            const { resolveAssetTiming, applyResolvedAssetTiming } = makeOperationsCallable(
                { resolveAssetTimingOperation, applyResolvedAssetTimingOperation },
                this.luminary,
                this.dispatch,
                this.getState,
            );
            const timingPlan = timingRequested
                ? planAssetTiming(
                      resolveAssetTiming(scene, asset, args),
                      args,
                      iconSourceData !== undefined && !iconAlreadyApplied,
                  )
                : undefined;
            if (timingPlan && 'error' in timingPlan) {
                return `Error: ${timingPlan.error}`;
            }

            if (iconSourceData && !iconAlreadyApplied) {
                try {
                    asset.replace(iconSourceData);
                } catch (error) {
                    return `Error: data wrong format for asset ${asset_name} in scene ${sceneShortId}.`;
                }
                changes.push(`set media for ${asset_name} to ${media_id}`);
            }

            if (changes.length > 0) {
                this.recordSceneFill(scene_index, 'icon');
            }

            if (timingPlan && 'skipNote' in timingPlan) {
                changes.push(timingPlan.skipNote);
            }
            if (timingPlan && 'timing' in timingPlan) {
                const applied = withTimingNote(
                    applyResolvedAssetTiming(
                        scene,
                        asset,
                        timingPlan.timing,
                        timingPlan.args,
                        parentActionGroup,
                    ),
                    timingPlan.note,
                );
                changes.push('warning' in applied ? applied.warning : applied.change);
            }

            if (changes.length > 0) {
                this.dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(this.luminary),
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
            }

            return `Success: Updated ${changes.join(', ')} for asset ${asset_name} in scene ${sceneShortId}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting media: ${errorMessage}`;
        }
    }
}

const SetIconResultRenderer: React.FC<CustomRendererComponentProps> = ({
    isLoading,
    result,
    toolCall,
}) => {
    if (isLoading) {
        return null;
    }
    const args = toolCall ? JSON.parse(toolCall.function.arguments) : {};
    const mediaId = args.media_id;
    const iconRegistry = IconRegistry.getInstance();
    const iconData = mediaId ? iconRegistry.getItem(mediaId) : null;
    return (
        <div className={chatMessageTextStyles['chat-message-text']}>
            <MarkdownWithDiff>{result}</MarkdownWithDiff>
            {iconData && (
                <div>
                    {React.createElement(BackgroundAsset, {
                        searchContext: null,
                        media: iconData,
                        media_id: 0,
                        result_index: 0,
                    })}
                </div>
            )}
        </div>
    );
};
